import { ButtonsContainer, Container } from './styles';
import { useAppDispatch, useAppSelector } from 'shared/lib/store';
import {
	selectBookingApplicationMod,
	selectBookingThreadMod,
	selectOrderBookingData,
	selectOrderDetail,
	useOrderState,
} from 'entities/orders';
import { FC, useCallback, useMemo, useState } from 'react';
import { BookingProps } from '../types';
import { withAsyncActionFeedback } from 'shared/ui/asyncAction';
import { ButtonContained } from 'shared/ui/buttons/buttonContained';
import { ModalError, ModalNotification, ModalProvider } from 'shared/ui/modal';
import { useBookingEffects } from 'widgets/booking/lib/hooks/useBookingEffects';
import { useClientForm } from 'features/booking/BookingClientForm';
import { OrderDetailWaitingConfirm } from 'widgets/orderDetail/ui/buttons/orderDetailWaitingConfirm';
import { OrderDetailRemoveConfirm } from 'widgets/orderDetail/ui/buttons/orderDetailRemoveConfirm';
import { selectHallmapSelectedTables } from 'entities/hallmap';
import { useBookingReadyCheck } from 'widgets/booking/lib/hooks/useBookingReadyCheck';
import { useBookingSlots, useBookingTimeExpired } from 'features/booking/slots';
import { selectDisableBookingTime, useTimeslot } from 'entities/toolbar/timeslots';
import { compareOrderChanges } from 'widgets/booking/lib/utils/compareOrderChanges';
import { getOrdersNext } from 'entities/orders/model/ordersThunk';
import { CustomError } from 'shared/api';
import { HttpStatusCode } from 'axios';
import { OrderDetailResetConfirm } from 'widgets/orderDetail/ui/buttons/orderDetailResetConfirm';
import { existsChanges } from 'widgets/booking/lib/utils/existsChanges';
import { selectSettings } from 'entities/settings/model/selector';

interface FooterProps extends BookingProps {
	toProhibitClose: (isClose?: boolean) => void;
}

export const Footer: FC<FooterProps> = ({ toProhibitClose }) => {
  const ActionButtonWithFeedback = withAsyncActionFeedback(ButtonContained, true);

	const dispatch = useAppDispatch();
	const bookingData = useAppSelector(selectOrderBookingData);
	const bookingThreadMod = useAppSelector(selectBookingThreadMod);
	const orderDetail = useAppSelector(selectOrderDetail);
	const selectedResources = useAppSelector(selectHallmapSelectedTables);
	const { isWaitingList } = useOrderState(orderDetail);
	const applicationMod = useAppSelector(selectBookingApplicationMod);
	const [requestError, setRequestError] = useState<CustomError | null>(null);
	const settings = useAppSelector(selectSettings);
  const { toTodayTimeSlot } = useTimeslot();

	const {
		isDisabledBooking,
		saveOrderOrBook,
		addToWaitingList,
		removeFromWaitingList,
		successClose,
		getWaitingOrdersList,
		bookingError,
		clearError,
	} = useBookingEffects(toProhibitClose);

	const { bookingBtnIsActive } = useClientForm();

	const waitingListOnSuccess = () => {
		successClose();
		dispatch(getOrdersNext({}));
		getWaitingOrdersList();
	};

	const { readyBookingCheck, resourcesCompare } = useBookingReadyCheck();
	const { isBookingTimeExpired } = useBookingTimeExpired();
	const disableBookingTime = useAppSelector(selectDisableBookingTime);
	const { currentActive } = useBookingSlots();

	const changeRequestError = (error: CustomError) => {
		if (error.status === 0 || error.status === HttpStatusCode.BadRequest || error.status.toString().startsWith('5')) {
			setRequestError(error);
		}
	}

	const checkDisabledBookingTime = useCallback(() => {
		const isDisabled =
			disableBookingTime &&
			currentActive &&
			disableBookingTime.date !== currentActive;

		if (isDisabled && selectedResources.length) {
			return false;
		} else if (disableBookingTime || !selectedResources.length) {
			return true;
		}
	}, [disableBookingTime, currentActive, selectedResources]);

	const buttonDisabled = useMemo(() => {
		if (applicationMod) {
			if (compareOrderChanges(orderDetail, bookingData)) {
				return true;
			}
			return false;
		}

		if (bookingData.id && bookingBtnIsActive) {
			return true;
		}

		if (isDisabledBooking) {
			return true;
		}

		if (isBookingTimeExpired()) {
			return true;
		}

    // console.log('compareOrderChanges(orderDetail, bookingData)',compareOrderChanges(orderDetail, bookingData))
		if (
			!applicationMod &&
			compareOrderChanges(orderDetail, bookingData) &&
			resourcesCompare && !isWaitingList
		) {
			return true;
		}

		checkDisabledBookingTime();
	}, [
		bookingBtnIsActive,
		bookingData,
		isDisabledBooking,
		applicationMod,
		checkDisabledBookingTime,
		isBookingTimeExpired,
		orderDetail,
		resourcesCompare,
		isWaitingList
	]);

	const nameForBookingBtn = useMemo(() => {
		if (applicationMod) {
			if (bookingData.id) {
				return 'Сохранить изменения';
			}

			return 'Сохранить заявку';
		}

		if (bookingData.id) {
			if (readyBookingCheck()) {
				if (isWaitingList) {
					return 'Создать бронирование';
				}

				return 'Изменить бронирование';
			}

			return 'Сохранить изменения';
		} else {
			if (bookingThreadMod) {
				return 'Разместить';
			} else {
				return 'Забронировать';
			}
		}
	}, [
		bookingData,
		bookingThreadMod,
		isWaitingList,
		applicationMod,
		readyBookingCheck,
	]);

	const hasReset = useMemo(() => {
		if (orderDetail) {
			return false;
		} else {
			if (applicationMod) {
				if (existsChanges(bookingData, settings?.default_participants_count)) {
					return true;
				}
			} else {
				if (!buttonDisabled || existsChanges(bookingData, settings?.default_participants_count)) {
					return true;
				}
			}
		}

		return false;
	}, [orderDetail, settings, applicationMod, bookingData, buttonDisabled]);

  const onClose = () => {
    toTodayTimeSlot()
  }

	return (
		<Container>
			<ActionButtonWithFeedback
				disabled={buttonDisabled}
				onClick={saveOrderOrBook}
				onError={changeRequestError}
				disableErrorToast
			>
				{nameForBookingBtn}
			</ActionButtonWithFeedback>

			{Boolean(requestError) && (
				<ModalProvider openByDefault>
					<ModalError
						error={requestError}
						onClose={() => setRequestError(null)}
					/>
				</ModalProvider>
			)}

			{Boolean(bookingError) && (
				<ModalProvider openByDefault>
					<ModalNotification
						title={bookingError}
						onClose={clearError}
					/>
				</ModalProvider>
			)}
			<ButtonsContainer>
				{!isWaitingList && (
					<ModalProvider>
						<OrderDetailWaitingConfirm
							dispatchToWaitingList={addToWaitingList}
							onSuccessFetchData={waitingListOnSuccess}
							person={bookingData?.person}
						/>
					</ModalProvider>
				)}
				{hasReset && (
					<ModalProvider>
						<OrderDetailResetConfirm onClose={onClose}/>
					</ModalProvider>
				)}
			</ButtonsContainer>
			{isWaitingList && (
				<ModalProvider>
					<OrderDetailRemoveConfirm
						removeFromWaitingList={removeFromWaitingList}
						onSuccessFetchData={waitingListOnSuccess}
						person={bookingData?.person}
            onClose={onClose}
					/>
				</ModalProvider>
			)}
		</Container>
	);
};
